import { Head, Link, router } from "@inertiajs/react"
import { useCallback, useEffect, useMemo, useState } from "react"
import { AppLayout } from "../../components/layout/AppLayout"
import { Button } from "../../components/ui/button"
import { Checkbox } from "../../components/ui/checkbox"
import { Input } from "../../components/ui/input"
import { Label } from "../../components/ui/label"
import type { FiscalSettingRates } from "../../lib/invoiceTributos"
import {
  computeInvoiceTributosBreakdown,
  matchFiscalSettingForMunicipio
} from "../../lib/invoiceTributos"

type ClientOption = {
  id: number
  company_id: number | null
  name: string
  municipio: string
  codigo_sap: string
  cr: string
  omie_n_cod_cli?: number | null
  cadastro_minimo_omie?: boolean
  sem_configuracao_fiscal?: boolean
  municipios: string[]
  fiscal_settings: FiscalSettingRates[]
}

type CompanyOption = {
  id: number
  name: string
  cnpj: string
  omie_servicos: { id: number; label: string }[]
  omie_contas_correntes: { id: number; label: string }[]
}

type InvoiceRow = {
  uid: string
  client_id: number
  client_name: string
  municipio: string
  codigo_sap: string
  cr: string
  medicao: string
  periodo_inicio: string
  periodo_fim: string
  valor: string
  omie_servico_id: string
  omie_conta_corrente_id: string
  payment_term_id: string
  selected: boolean
  fiscal_setting?: FiscalSettingRates
}

type HistoricoEntry = {
  ultimos: number[]
  media: number
}

type PaymentTermOption = {
  id: number
  label: string
  name: string
  days: number
}

type Props = {
  companies: CompanyOption[]
  clients: ClientOption[]
  payment_terms?: PaymentTermOption[]
}

function currency(value: number) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value)
}

function toNumber(value: string) {
  const parsed = Number(value.replace(",", "."))
  return Number.isFinite(parsed) ? parsed : 0
}

function valorAnomalia(valor: number, historico: HistoricoEntry | undefined): "alto" | "baixo" | null {
  if (!historico || historico.ultimos.length === 0 || valor <= 0) return null
  const media = historico.media
  if (media <= 0) return null
  if (valor > media * 1.5) return "alto"
  if (valor < media * 0.5) return "baixo"
  return null
}

export default function EmissaoMassa({ companies, clients, payment_terms }: Props) {
  const [companyId, setCompanyId] = useState("")
  const [clientSearch, setClientSearch] = useState("")
  const [selectedClientIds, setSelectedClientIds] = useState<Set<number>>(new Set())
  const [rows, setRows] = useState<InvoiceRow[]>([])
  const [historico, setHistorico] = useState<Record<string, HistoricoEntry>>({})
  const [submitting, setSubmitting] = useState(false)
  const [globalPeriodoInicio, setGlobalPeriodoInicio] = useState("")
  const [globalPeriodoFim, setGlobalPeriodoFim] = useState("")
  const [paymentTermId, setPaymentTermId] = useState("")
  const [flashMessage, setFlashMessage] = useState<{ type: "success" | "error"; text: string } | null>(null)

  const selectedCompany = useMemo(
    () => companies.find((c) => String(c.id) === companyId),
    [companies, companyId]
  )

  const clientsDaEmpresa = useMemo(() => {
    if (!companyId) return []
    return clients.filter((c) => c.company_id != null && String(c.company_id) === companyId)
  }, [clients, companyId])

  const filteredClients = useMemo(() => {
    let list = clientsDaEmpresa
    const search = clientSearch.trim().toLowerCase()
    if (!search) return list
    return list.filter((c) => {
      const hay = [c.name, c.codigo_sap, c.cr, c.municipio, ...c.municipios].join(" ").toLowerCase()
      return (
        hay.includes(search) ||
        hay.split(/\s+/).some((w) => w.startsWith(search)) ||
        c.municipios.some((m) => m.toLowerCase().includes(search))
      )
    })
  }, [clientsDaEmpresa, clientSearch])

  const toggleClient = useCallback(
    (clientId: number) => {
      setSelectedClientIds((prev) => {
        const next = new Set(prev)
        if (next.has(clientId)) {
          next.delete(clientId)
        } else {
          next.add(clientId)
        }
        return next
      })
    },
    []
  )

  useEffect(() => {
    const newRows: InvoiceRow[] = []
    for (const clientId of selectedClientIds) {
      const client = clients.find((c) => c.id === clientId)
      if (!client) continue

      const municipios = client.municipios.length > 0 ? client.municipios : [client.municipio].filter(Boolean)
      for (const mun of municipios) {
        const fiscal = matchFiscalSettingForMunicipio(client.fiscal_settings, mun, companyId || undefined)
        if (!fiscal) continue

        const existing = rows.find(
          (r) => r.client_id === clientId && r.municipio === mun
        )

        let omieServicoId = ""
        let omieContaId = ""
        if (fiscal.omie_servico_id) omieServicoId = String(fiscal.omie_servico_id)
        if (fiscal.omie_conta_corrente_id) omieContaId = String(fiscal.omie_conta_corrente_id)
        if (!omieServicoId && selectedCompany?.omie_servicos.length === 1) {
          omieServicoId = String(selectedCompany.omie_servicos[0].id)
        }
        if (!omieContaId && selectedCompany?.omie_contas_correntes.length === 1) {
          omieContaId = String(selectedCompany.omie_contas_correntes[0].id)
        }

        if (existing) {
          newRows.push({ ...existing, fiscal_setting: fiscal })
        } else {
          newRows.push({
            uid: `${clientId}-${mun}`,
            client_id: clientId,
            client_name: client.name,
            municipio: mun,
            codigo_sap: client.codigo_sap || "",
            cr: client.cr || "",
            medicao: "",
            periodo_inicio: globalPeriodoInicio,
            periodo_fim: globalPeriodoFim,
            valor: "",
            omie_servico_id: omieServicoId,
            omie_conta_corrente_id: omieContaId,
            payment_term_id: paymentTermId,
            selected: true,
            fiscal_setting: fiscal
          })
        }
      }
    }

    newRows.sort((a, b) => {
      const nameComp = a.client_name.localeCompare(b.client_name, "pt-BR")
      if (nameComp !== 0) return nameComp
      return a.municipio.localeCompare(b.municipio, "pt-BR")
    })

    setRows(newRows)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientIds, companyId, clients])

  useEffect(() => {
    if (rows.length === 0) return
    const pairs = rows.map((r) => ({ client_id: r.client_id, municipio: r.municipio }))
    const uniquePairs = pairs.filter(
      (p, i, arr) => arr.findIndex((x) => x.client_id === p.client_id && x.municipio === p.municipio) === i
    )

    fetch("/notas-fiscais/emissao-em-massa/historico", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ""
      },
      body: JSON.stringify({ pairs: uniquePairs })
    })
      .then((res) => res.json())
      .then((data) => setHistorico(data))
      .catch(() => {})
  }, [rows.length > 0 ? rows.map((r) => `${r.client_id}|${r.municipio}`).join(",") : ""])

  function updateRow(uid: string, field: keyof InvoiceRow, value: string | boolean) {
    setRows((prev) => prev.map((r) => (r.uid === uid ? { ...r, [field]: value } : r)))
  }

  function applyGlobalPeriodo() {
    setRows((prev) =>
      prev.map((r) => ({
        ...r,
        periodo_inicio: globalPeriodoInicio || r.periodo_inicio,
        periodo_fim: globalPeriodoFim || r.periodo_fim
      }))
    )
  }

  function applyGlobalPaymentTerm() {
    if (!paymentTermId) return
    setRows((prev) => prev.map((r) => ({ ...r, payment_term_id: paymentTermId })))
  }

  function toggleAllRows(checked: boolean) {
    setRows((prev) => prev.map((r) => ({ ...r, selected: checked })))
  }

  const selectedRows = rows.filter((r) => r.selected)
  const allSelected = rows.length > 0 && rows.every((r) => r.selected)

  function handleSubmit() {
    if (!companyId || selectedRows.length === 0) return
    setSubmitting(true)

    const invoices = selectedRows.map((r) => ({
      client_id: r.client_id,
      municipio: r.municipio,
      codigo_sap: r.codigo_sap,
      cr: r.cr,
      medicao: r.medicao,
      periodo_inicio: r.periodo_inicio,
      periodo_fim: r.periodo_fim,
      valor: r.valor,
      omie_servico_id: r.omie_servico_id || null,
      omie_conta_corrente_id: r.omie_conta_corrente_id || null,
      payment_term_id: r.payment_term_id || null
    }))

    router.post(
      "/notas-fiscais/emissao-em-massa",
      { company_id: companyId, invoices },
      {
        onFinish: () => setSubmitting(false),
        onError: () => setSubmitting(false)
      }
    )
  }

  const servicosOmie = selectedCompany?.omie_servicos ?? []
  const contasOmie = selectedCompany?.omie_contas_correntes ?? []

  return (
    <>
      <Head title="Emissão em Massa" />
      <AppLayout>
        <div className="mx-auto w-full max-w-[1400px] p-8">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white">Emissão em Massa</h1>
              <p className="text-slate-400">
                Selecione clientes e municípios para emitir várias notas de uma vez.
              </p>
            </div>
            <Link
              href="/notas-fiscais/nova"
              className="rounded-md border border-slate-700 bg-slate-900 px-4 py-2 text-sm font-medium text-slate-200 transition-colors hover:bg-slate-800"
            >
              Emissão individual
            </Link>
          </div>

          {flashMessage && (
            <div
              className={`mb-4 rounded-md border px-4 py-3 text-sm ${
                flashMessage.type === "error"
                  ? "border-rose-800 bg-rose-950/40 text-rose-200"
                  : "border-emerald-800 bg-emerald-950/40 text-emerald-200"
              }`}
            >
              {flashMessage.text}
            </div>
          )}

          <div className="space-y-6">
            {/* Empresa */}
            <div className="rounded-xl border border-slate-800 bg-slate-900 p-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="company_id">Empresa</Label>
                  <select
                    id="company_id"
                    value={companyId}
                    onChange={(e) => {
                      setCompanyId(e.target.value)
                      setSelectedClientIds(new Set())
                      setRows([])
                      setClientSearch("")
                    }}
                    className="flex h-10 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100"
                  >
                    <option value="">Selecione a empresa</option>
                    {companies.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.name} ({company.cnpj})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label>Período (aplicar a todas)</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="date"
                      value={globalPeriodoInicio}
                      onChange={(e) => setGlobalPeriodoInicio(e.target.value)}
                      className="flex-1"
                    />
                    <span className="text-slate-500">a</span>
                    <Input
                      type="date"
                      value={globalPeriodoFim}
                      onChange={(e) => setGlobalPeriodoFim(e.target.value)}
                      className="flex-1"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Condição de pagamento</Label>
                  <select
                    value={paymentTermId}
                    onChange={(e) => setPaymentTermId(e.target.value)}
                    className="flex h-10 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100"
                  >
                    <option value="">Selecione a condição</option>
                    {(payment_terms ?? []).map((pt) => (
                      <option key={pt.id} value={pt.id}>
                        {pt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  className="border-slate-700 bg-transparent"
                  disabled={!globalPeriodoInicio && !globalPeriodoFim}
                  onClick={applyGlobalPeriodo}
                >
                  Aplicar período a todas
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="border-slate-700 bg-transparent"
                  disabled={!paymentTermId}
                  onClick={applyGlobalPaymentTerm}
                >
                  Aplicar condição a todas
                </Button>
              </div>
            </div>

            {/* Seleção de clientes */}
            {companyId && (
              <div className="rounded-xl border border-slate-800 bg-slate-900 p-6">
                <h2 className="mb-3 text-lg font-semibold text-white">Clientes</h2>
                <p className="mb-4 text-xs text-slate-400">
                  Selecione os clientes para emissão. Cada município com configuração fiscal gera uma linha na tabela.
                </p>
                <Input
                  placeholder="Buscar cliente por nome ou município..."
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  className="mb-3 max-w-md"
                />
                <div className="max-h-64 overflow-y-auto rounded-md border border-slate-800">
                  {filteredClients.length === 0 ? (
                    <p className="px-4 py-6 text-center text-sm text-slate-500">Nenhum cliente encontrado.</p>
                  ) : (
                    <table className="w-full text-left text-sm">
                      <thead className="sticky top-0 border-b border-slate-800 bg-slate-900">
                        <tr className="text-xs uppercase tracking-wide text-slate-400">
                          <th className="w-10 px-3 py-2" />
                          <th className="px-3 py-2 font-medium">Cliente</th>
                          <th className="px-3 py-2 font-medium">Municípios (configs)</th>
                          <th className="px-3 py-2 font-medium">SAP</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800/50">
                        {filteredClients.map((client) => {
                          const checked = selectedClientIds.has(client.id)
                          const allMunicipios = client.municipios.length > 0 ? client.municipios : [client.municipio].filter(Boolean)
                          const validMunicipios = allMunicipios.filter(
                            (m) => matchFiscalSettingForMunicipio(client.fiscal_settings, m, companyId || undefined)
                          )
                          const totalCount = allMunicipios.length
                          const validCount = validMunicipios.length
                          return (
                            <tr
                              key={client.id}
                              className={`cursor-pointer transition-colors ${checked ? "bg-brand-950/20" : "hover:bg-slate-800/40"}`}
                              onClick={() => toggleClient(client.id)}
                            >
                              <td className="px-3 py-2">
                                <Checkbox
                                  checked={checked}
                                  onChange={() => toggleClient(client.id)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </td>
                              <td className="px-3 py-2 text-slate-200">{client.name}</td>
                              <td className="px-3 py-2 text-slate-400">
                                {validCount > 0 ? (
                                  <span>
                                    {validCount} {validCount === 1 ? "município" : "municípios"}
                                    {validCount < totalCount && (
                                      <span className="ml-1 text-amber-500" title={`${totalCount - validCount} município(s) sem config. fiscal para esta empresa`}>
                                        ({totalCount - validCount} sem config.)
                                      </span>
                                    )}
                                  </span>
                                ) : totalCount > 0 ? (
                                  <span className="text-amber-500" title="Nenhum município com config. fiscal para esta empresa">
                                    {totalCount} mun. sem config. p/ empresa
                                  </span>
                                ) : (
                                  <span className="text-amber-500">sem config. fiscal</span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-slate-500">{client.codigo_sap || "—"}</td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
                {selectedClientIds.size > 0 && (
                  <p className="mt-2 text-xs text-slate-400">
                    {selectedClientIds.size} cliente(s) selecionado(s) — {rows.length} linha(s) na tabela
                  </p>
                )}
              </div>
            )}

            {/* Tabela de notas */}
            {rows.length > 0 && (
              <div className="rounded-xl border border-slate-800 bg-slate-900 p-6">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-white">
                    Notas a emitir ({selectedRows.length}/{rows.length})
                  </h2>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 text-sm text-slate-300">
                      <Checkbox checked={allSelected} onChange={(e) => toggleAllRows(e.target.checked)} />
                      Selecionar todas
                    </label>
                  </div>
                </div>
                <div className="overflow-x-auto rounded-md border border-slate-800">
                  <table className="w-full min-w-[1200px] text-left text-sm">
                    <thead>
                      <tr className="border-b border-slate-800 bg-slate-950/80 text-xs uppercase tracking-wide text-slate-400">
                        <th className="w-10 px-2 py-2" />
                        <th className="px-2 py-2 font-medium">Cliente</th>
                        <th className="px-2 py-2 font-medium">Município</th>
                        <th className="px-2 py-2 font-medium">SAP</th>
                        <th className="px-2 py-2 font-medium">CR</th>
                        <th className="w-24 px-2 py-2 font-medium">Medição</th>
                        <th className="w-28 px-2 py-2 font-medium">Início</th>
                        <th className="w-28 px-2 py-2 font-medium">Fim</th>
                        <th className="w-28 px-2 py-2 font-medium">Valor</th>
                        <th className="w-32 px-2 py-2 font-medium">Pagamento</th>
                        <th className="w-24 px-2 py-2 font-medium">Tributos</th>
                        <th className="w-10 px-2 py-2" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800/50">
                      {rows.map((row) => {
                        const valorNum = toNumber(row.valor)
                        const tributos = computeInvoiceTributosBreakdown(valorNum, row.fiscal_setting)
                        const key = `${row.client_id}|${row.municipio}`
                        const hist = historico[key]
                        const anomalia = valorAnomalia(valorNum, hist)

                        return (
                          <tr
                            key={row.uid}
                            className={`transition-colors ${row.selected ? "bg-slate-900/40" : "bg-slate-950/30 opacity-50"}`}
                          >
                            <td className="px-2 py-1.5">
                              <Checkbox
                                checked={row.selected}
                                onChange={(e) => updateRow(row.uid, "selected", e.target.checked)}
                              />
                            </td>
                            <td className="max-w-[160px] truncate px-2 py-1.5 text-slate-200" title={row.client_name}>
                              {row.client_name}
                            </td>
                            <td className="max-w-[140px] truncate px-2 py-1.5 text-slate-300" title={row.municipio}>
                              {row.municipio}
                            </td>
                            <td className="px-2 py-1.5">
                              <input
                                type="text"
                                value={row.codigo_sap}
                                onChange={(e) => updateRow(row.uid, "codigo_sap", e.target.value)}
                                className="h-8 w-20 rounded border border-slate-700 bg-slate-950 px-2 text-xs text-slate-100"
                              />
                            </td>
                            <td className="px-2 py-1.5">
                              <input
                                type="text"
                                value={row.cr}
                                onChange={(e) => updateRow(row.uid, "cr", e.target.value)}
                                className="h-8 w-20 rounded border border-slate-700 bg-slate-950 px-2 text-xs text-slate-100"
                              />
                            </td>
                            <td className="px-2 py-1.5">
                              <input
                                type="number"
                                step="0.01"
                                value={row.medicao}
                                onChange={(e) => updateRow(row.uid, "medicao", e.target.value)}
                                className="h-8 w-full rounded border border-slate-700 bg-slate-950 px-2 text-xs text-slate-100"
                              />
                            </td>
                            <td className="px-2 py-1.5">
                              <input
                                type="date"
                                value={row.periodo_inicio}
                                onChange={(e) => updateRow(row.uid, "periodo_inicio", e.target.value)}
                                className="h-8 w-full rounded border border-slate-700 bg-slate-950 px-2 text-xs text-slate-100"
                              />
                            </td>
                            <td className="px-2 py-1.5">
                              <input
                                type="date"
                                value={row.periodo_fim}
                                onChange={(e) => updateRow(row.uid, "periodo_fim", e.target.value)}
                                className="h-8 w-full rounded border border-slate-700 bg-slate-950 px-2 text-xs text-slate-100"
                              />
                            </td>
                            <td className="px-2 py-1.5">
                              <div className="relative">
                                <input
                                  type="number"
                                  step="0.01"
                                  value={row.valor}
                                  onChange={(e) => updateRow(row.uid, "valor", e.target.value)}
                                  className={`h-8 w-full rounded border px-2 text-xs text-slate-100 ${
                                    anomalia
                                      ? "border-amber-600 bg-amber-950/30"
                                      : "border-slate-700 bg-slate-950"
                                  }`}
                                />
                              </div>
                            </td>
                            <td className="px-2 py-1.5">
                              <select
                                value={row.payment_term_id}
                                onChange={(e) => updateRow(row.uid, "payment_term_id", e.target.value)}
                                className="h-8 w-full rounded border border-slate-700 bg-slate-950 px-1 text-xs text-slate-100"
                              >
                                <option value="">—</option>
                                {(payment_terms ?? []).map((pt) => (
                                  <option key={pt.id} value={pt.id}>
                                    {pt.name}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="px-2 py-1.5 text-xs tabular-nums text-slate-400">
                              {valorNum > 0 ? currency(tributos.total) : "—"}
                            </td>
                            <td className="px-2 py-1.5">
                              {anomalia && (
                                <div className="group relative">
                                  <span
                                    className="inline-flex cursor-help text-amber-400"
                                    title={
                                      anomalia === "alto"
                                        ? `Valor muito acima da média das últimas 3 notas (${currency(hist!.media)})`
                                        : `Valor muito abaixo da média das últimas 3 notas (${currency(hist!.media)})`
                                    }
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      viewBox="0 0 24 24"
                                      fill="currentColor"
                                      className="h-5 w-5"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.499-2.599 4.499H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.004zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  </span>
                                  <div className="pointer-events-none absolute bottom-full right-0 z-50 mb-2 w-56 rounded-md border border-slate-700 bg-slate-800 p-3 text-xs text-slate-200 opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
                                    <p className="mb-1 font-semibold text-amber-300">
                                      {anomalia === "alto" ? "Valor acima do esperado" : "Valor abaixo do esperado"}
                                    </p>
                                    <p className="text-slate-400">
                                      Média das últimas {hist!.ultimos.length} notas: {currency(hist!.media)}
                                    </p>
                                    <div className="mt-1 space-y-0.5 text-slate-500">
                                      {hist!.ultimos.map((v, i) => (
                                        <p key={i}>
                                          Nota {i + 1}: {currency(v)}
                                        </p>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              )}
                              {!anomalia && hist && valorNum > 0 && (
                                <div className="group relative">
                                  <span
                                    className="inline-flex cursor-help text-emerald-500/60"
                                    title={`Dentro da faixa esperada (média: ${currency(hist.media)})`}
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      viewBox="0 0 24 24"
                                      fill="currentColor"
                                      className="h-4 w-4"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  </span>
                                </div>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Serviço e conta Omie globais */}
                {selectedCompany && (servicosOmie.length > 1 || contasOmie.length > 1) && (
                  <div className="mt-4 rounded-lg border border-slate-800 bg-slate-950/60 p-4">
                    <h3 className="mb-2 text-sm font-semibold text-slate-200">
                      Serviço e conta Omie (padrão do lote)
                    </h3>
                    <p className="mb-3 text-xs text-slate-500">
                      As notas usarão o serviço/conta definido na configuração fiscal de cada
                      município. Se quiser sobrescrever todas, altere abaixo e clique em aplicar.
                    </p>
                    <div className="flex flex-wrap items-end gap-4">
                      {servicosOmie.length > 1 && (
                        <div className="space-y-1">
                          <Label className="text-xs">Serviço Omie</Label>
                          <select
                            id="batch_omie_servico"
                            className="flex h-9 w-64 rounded-md border border-slate-700 bg-slate-950 px-3 py-1 text-sm text-slate-100"
                            defaultValue=""
                            onChange={(e) => {
                              const val = e.target.value
                              if (!val) return
                              setRows((prev) => prev.map((r) => ({ ...r, omie_servico_id: val })))
                            }}
                          >
                            <option value="">Manter individual</option>
                            {servicosOmie.map((s) => (
                              <option key={s.id} value={s.id}>
                                {s.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                      {contasOmie.length > 1 && (
                        <div className="space-y-1">
                          <Label className="text-xs">Conta corrente Omie</Label>
                          <select
                            id="batch_omie_conta"
                            className="flex h-9 w-64 rounded-md border border-slate-700 bg-slate-950 px-3 py-1 text-sm text-slate-100"
                            defaultValue=""
                            onChange={(e) => {
                              const val = e.target.value
                              if (!val) return
                              setRows((prev) => prev.map((r) => ({ ...r, omie_conta_corrente_id: val })))
                            }}
                          >
                            <option value="">Manter individual</option>
                            {contasOmie.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Resumo e submit */}
                <div className="mt-6 flex items-center justify-between">
                  <div className="text-sm text-slate-400">
                    <span className="font-medium text-slate-200">{selectedRows.length}</span> nota(s) selecionada(s)
                    {selectedRows.length > 0 && (
                      <>
                        {" · "}
                        Total bruto:{" "}
                        <span className="font-medium text-slate-200">
                          {currency(selectedRows.reduce((sum, r) => sum + toNumber(r.valor), 0))}
                        </span>
                      </>
                    )}
                  </div>
                  <Button
                    type="button"
                    disabled={submitting || selectedRows.length === 0 || !companyId}
                    onClick={handleSubmit}
                    className="min-w-[200px]"
                  >
                    {submitting
                      ? "Criando notas..."
                      : `Emitir ${selectedRows.length} nota(s)`}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </AppLayout>
    </>
  )
}
